import { supabase } from '../lib/supabase';

/**
 * Unified Dashboard Service
 * Common backend functionality for all dashboard types (Brand, Creator, Agency, Admin)
 */

export interface DashboardMetrics {
  totalCampaigns: number;
  activeCampaigns: number;
  totalApplications: number;
  pendingApplications: number;
  totalSpend: number;
  totalBudget: number;
  averageEngagement: number;
  growthRate: number;
}

export interface ActivityLog {
  id: string;
  userId: string;
  activityType: string;
  title: string;
  description: string;
  metadata?: any;
  createdAt: string;
}

export interface NotificationItem {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  link?: string;
  createdAt: string;
}

class DashboardService {
  /**
   * Get comprehensive dashboard metrics based on user role
   */
  async getDashboardMetrics(userId: string, role: 'brand' | 'creator' | 'agency' | 'admin'): Promise<DashboardMetrics> {
    try {
      let data;
      
      switch (role) {
        case 'brand':
          data = await this.getBrandMetrics(userId);
          break;
        case 'creator':
          data = await this.getCreatorMetrics(userId);
          break;
        case 'agency':
          data = await this.getAgencyMetrics(userId);
          break;
        case 'admin':
          data = await this.getAdminMetrics(userId);
          break;
        default:
          throw new Error(`Invalid role: ${role}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      return this.getEmptyMetrics();
    }
  }

  /**
   * Get brand-specific metrics
   */
  private async getBrandMetrics(userId: string): Promise<DashboardMetrics> {
    const { data, error } = await supabase.rpc('get_brand_dashboard_metrics', {
      brand_user_id: userId
    });

    if (error) throw error;

    const metrics = Array.isArray(data) ? data[0] : data;
    
    return {
      totalCampaigns: metrics?.total_campaigns || 0,
      activeCampaigns: metrics?.active_campaigns || 0,
      totalApplications: metrics?.total_applications || 0,
      pendingApplications: metrics?.pending_applications || 0,
      totalSpend: metrics?.total_spend || 0,
      totalBudget: metrics?.total_budget || 0,
      averageEngagement: metrics?.average_engagement || 0,
      growthRate: metrics?.growth_rate || 0,
    };
  }

  /**
   * Get creator-specific metrics
   */
  private async getCreatorMetrics(userId: string): Promise<DashboardMetrics> {
    const { data, error } = await supabase.rpc('get_creator_dashboard_metrics', {
      creator_user_id: userId
    });

    if (error) throw error;

    const metrics = Array.isArray(data) ? data[0] : data;
    
    return {
      totalCampaigns: metrics?.total_campaigns || 0,
      activeCampaigns: metrics?.active_campaigns || 0,
      totalApplications: metrics?.total_applications || 0,
      pendingApplications: metrics?.pending_applications || 0,
      totalSpend: metrics?.total_earnings || 0,
      totalBudget: metrics?.potential_earnings || 0,
      averageEngagement: metrics?.average_engagement || 0,
      growthRate: metrics?.growth_rate || 0,
    };
  }

  /**
   * Get agency-specific metrics
   */
  private async getAgencyMetrics(userId: string): Promise<DashboardMetrics> {
    const { data, error } = await supabase.rpc('get_agency_dashboard_metrics', {
      agency_user_id: userId
    });

    if (error) throw error;

    const metrics = Array.isArray(data) ? data[0] : data;
    
    return {
      totalCampaigns: metrics?.total_campaigns || 0,
      activeCampaigns: metrics?.active_campaigns || 0,
      totalApplications: metrics?.total_applications || 0,
      pendingApplications: metrics?.pending_approvals || 0,
      totalSpend: metrics?.total_spend || 0,
      totalBudget: metrics?.total_budget || 0,
      averageEngagement: metrics?.average_roi || 0,
      growthRate: metrics?.growth_rate || 0,
    };
  }

  /**
   * Get admin-specific metrics (system-wide)
   */
  private async getAdminMetrics(userId: string): Promise<DashboardMetrics> {
    const { data, error } = await supabase.rpc('get_admin_dashboard_metrics');

    if (error) throw error;

    const metrics = Array.isArray(data) ? data[0] : data;
    
    return {
      totalCampaigns: metrics?.total_campaigns || 0,
      activeCampaigns: metrics?.active_campaigns || 0,
      totalApplications: metrics?.total_applications || 0,
      pendingApplications: metrics?.pending_applications || 0,
      totalSpend: metrics?.total_platform_spend || 0,
      totalBudget: metrics?.total_platform_budget || 0,
      averageEngagement: metrics?.platform_engagement || 0,
      growthRate: metrics?.user_growth_rate || 0,
    };
  }

  /**
   * Get recent activity logs
   */
  async getRecentActivity(userId: string, limit: number = 10): Promise<ActivityLog[]> {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((log: any) => ({
        id: log.id,
        userId: log.user_id,
        activityType: log.activity_type,
        title: log.title,
        description: log.description,
        metadata: log.metadata,
        createdAt: log.created_at,
      }));
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      return [];
    }
  }

  /**
   * Log user activity
   */
  async logActivity(
    userId: string,
    activityType: string,
    title: string,
    description: string,
    metadata?: any
  ): Promise<void> {
    try {
      const { error } = await supabase.from('activity_logs').insert({
        user_id: userId,
        activity_type: activityType,
        title,
        description,
        metadata: metadata || {},
        created_at: new Date().toISOString(),
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  /**
   * Get notifications for user
   */
  async getNotifications(userId: string, limit: number = 20): Promise<NotificationItem[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((notif: any) => ({
        id: notif.id,
        userId: notif.user_id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        read: notif.read,
        link: notif.link,
        createdAt: notif.created_at,
      }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadNotificationCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error fetching unread notification count:', error);
      return 0;
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsRead(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  /**
   * Get empty metrics (fallback)
   */
  private getEmptyMetrics(): DashboardMetrics {
    return {
      totalCampaigns: 0,
      activeCampaigns: 0,
      totalApplications: 0,
      pendingApplications: 0,
      totalSpend: 0,
      totalBudget: 0,
      averageEngagement: 0,
      growthRate: 0,
    };
  }

  /**
   * Subscribe to real-time updates for dashboard
   */
  subscribeToUpdates(
    userId: string,
    role: string,
    callback: (payload: any) => void
  ) {
    const tables = this.getRelevantTables(role);
    const subscriptions: any[] = [];

    tables.forEach(table => {
      const subscription = supabase
        .channel(`${table}_changes_${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: table,
            filter: `user_id=eq.${userId}`,
          },
          callback
        )
        .subscribe();

      subscriptions.push(subscription);
    });

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }

  /**
   * Get relevant tables for real-time subscriptions based on role
   */
  private getRelevantTables(role: string): string[] {
    const commonTables = ['notifications', 'activity_logs'];
    
    switch (role) {
      case 'brand':
        return [...commonTables, 'campaigns', 'campaign_applications', 'campaign_invitations'];
      case 'creator':
        return [...commonTables, 'campaign_applications', 'campaign_invitations', 'creator_profiles'];
      case 'agency':
        return [...commonTables, 'agency_brand_relationships', 'agency_messages', 'agency_payments'];
      case 'admin':
        return [...commonTables, 'campaigns', 'users', 'user_profiles'];
      default:
        return commonTables;
    }
  }
}

export const dashboardService = new DashboardService();
